import React from 'react';
import { call, put } from 'redux-saga/effects';
import { takeLatest } from '../../../utils/reduxSaga';

import { JSONAPI_URLS, OUTSIDE_API, URLS } from '../../../consts/endpoints';
import { actions as reduxActions } from '../reducers';
import { actions as sagaActions, GET_SELECT_OPTIONS } from './types';
import { getData } from '../../../utils/http';
import {
  doesArrayHasLength,
  isArray,
  isArrayEmpty,
  isObject
} from '../../../utils/isDefined';
import getAvailableNumbersOptions from './getAvailableNumbersOptions';
import mapDefaultAddress from './mapDefaultAddress';
import orderBy from 'lodash.orderby';
import Optional from '../../../utils/optional';
import mapLicenses from '../../editUser/actions/mapLicenses';
import { mitelRequest } from '../../../utils/mitelRequest';

export function getAllSelectOptions({ connect30_domain, clientId, lang }) {
  return {
    type: sagaActions[GET_SELECT_OPTIONS],
    connect30_domain,
    clientId,
    lang
  };
}

export function* getSelectOptions({ connect30_domain, clientId, lang }) {
  yield put(reduxActions.setLoadingOptions(true));
  let accountsSelectsOptions = [],
    usersSelectOptions = [],
    publicNumbersSelectOptions = [],
    privateNumbersSelectOptions = [],
    languageOptions = [],
    account = '';

  try {
    const response = yield call(
      getData,
      `${OUTSIDE_API.CLIENTS}/${connect30_domain}/users`
    );
    usersSelectOptions = yield call(mapUsersForSelect, response);
  } catch {}

  try {
    const languagesResponse = yield call(
      getData,
      `${OUTSIDE_API.CLIENTS}/${connect30_domain}/languages`
    );

    const displayNames = new Intl.DisplayNames([lang || 'en'], {
      type: 'language'
    });

    languageOptions = Optional(languagesResponse)
      .or([])
      .map(lang => {
        return {
          value: lang.languageId,
          label:
            displayNames.of(lang.languageId.replace(/_/g, '-')) ||
            lang.languageId
        };
      });
  } catch {}

  const {
    publicNumbers,
    privateNumbers
  } = yield call(getAvailableNumbersOptions, { clientId, connect30_domain });

  publicNumbersSelectOptions = publicNumbers;
  privateNumbersSelectOptions = privateNumbers;

  try {
    const data = yield call(
      getData,
      `${URLS.QUOTE_CLIENTS}/${clientId}/accounts`
    );

    const { firstAccount, mappedAcounts } = yield call(
      createAccountsOptions,
      data
    );

    account = firstAccount;
    accountsSelectsOptions = mappedAcounts;
  } catch {}

  const fourthStep = isObject(account)
    ? mapDefaultAddress({
        account
      })
    : {};

  const licenses = yield call(
    getData,
    `${OUTSIDE_API.CLIENTS}/${connect30_domain}/licenses`
  );
  const mappedLicenses = yield call(mapLicenses, licenses);

  yield put(reduxActions.setAccount(account, fourthStep));

  const { data: groups } = yield call(mitelRequest, {
    options: {
      url: `${OUTSIDE_API.CLIENTS}/${connect30_domain}/user_groups`
    }
  });

  let allGroupsOptions = [];

  if (isArray(groups)) {
    allGroupsOptions = mapGroups(groups);
  }

  yield put(
    reduxActions.setSelectOptions({
      usersSelectOptions,
      publicNumbersSelectOptions,
      privateNumbersSelectOptions,
      accountsSelectsOptions,
      licenses: mappedLicenses,
      allGroupsOptions,
      languageOptions
    })
  );
  yield put(reduxActions.setLoadingOptions(false));
}

function* watchGetSelectOptions() {
  yield takeLatest(sagaActions[GET_SELECT_OPTIONS], getSelectOptions);
}

export default watchGetSelectOptions();

export function mapUsersForSelect(users = []) {
  if (!doesArrayHasLength(users)) {
    return [{ value: 'No users to copy', label: 'No users to copy' }];
  }

  const selectOptions = users
    .map(item => {
      return {
        ...item,
        value: `${item.name} - ${item.user_id}`,
        label: `${item.name} - ${item.user_id}`
      };
    })
    .sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1));

  return [{ value: '', label: '' }, ...selectOptions];
}

function createAccountsOptions(data = []) {
  let firstAccount = '';

  if (isArrayEmpty(data)) {
    return { firstAccount, mappedAcounts: [] };
  }

  const mappedAcounts = data.map(el => {
    const { name, accountNo, type } = el;
    return {
      value: `${name} - ${accountNo}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {type === 'CUSTOMER' ? (
            <i className="fas fa-handshake-alt"></i>
          ) : (
            <i className="fal fa-money-bill-alt"></i>
          )}
          <div style={{ marginLeft: '5px' }}>{`${name} - ${accountNo}`}</div>
        </div>
      ),
      shortLabel: `${name} - ${accountNo}`,
      ...el
    };
  });

  const customerAccounts = orderBy(
    mappedAcounts.filter(el => el.type === 'CUSTOMER'),
    'shortLabel'
  );
  const invoiceAccounts = orderBy(
    mappedAcounts.filter(el => el.type === 'INVOICE'),
    'shortLabel'
  );

  firstAccount = Optional(customerAccounts[0]).or(invoiceAccounts[0]);

  return {
    firstAccount,
    mappedAcounts: [...customerAccounts, ...invoiceAccounts]
  };
}

const mapGroups = (groups = []) => {
  return groups.map(group => {
    return { isUserGroup: false, label: group.name };
  });
};
